import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { currentLevel, LEVEL_DEGRADED } from "../field_network_health"
import { canDecodeHeicNatively } from "../field_image_compressor"

// Install Status page (card #4916) -- shows the Tasker (and, via the same
// app_log rows head office already reads, Mission Control) what the service
// worker's CRITICAL and SECONDARY install groups actually saved.
//
// Reads live state straight from the browser, the same way
// field_storage_detail_controller.js reads Cache Storage directly rather
// than asking the server (this is client-local state the server never sees).
// No HTML is built here -- every row already exists in the DOM
// (field/install/_item.html.erb); this only toggles text/classes on it.
//
// Targets:
//   item        -- one per manifest-declared page/asset row (data-url, data-kind)
//   retryAllBtn -- the Background group's "Retry now" button
const SHELL_CACHE_PREFIX = 'win-field-shell-'
// Must match ASSET_CACHE in field_service_worker.js.
const ASSET_CACHE = 'win-field-assets-v1'

const STATUS_SAVED     = { text: "Saved",              badge: "text-emerald-600 bg-emerald-50" }
const STATUS_FAILED    = { text: "Failed, will retry",  badge: "text-red-600 bg-red-50" }
const STATUS_WAITING   = { text: "Waiting for signal",  badge: "text-slate-500 bg-slate-100" }
const STATUS_DOWNLOADING = { text: "Downloading…",      badge: "text-sapphire bg-sapphire/10" }
const STATUS_CHECKING  = { text: "Checking...",         badge: "text-slate-400 bg-slate-50" }
// Card #4916 follow-up -- a browser that decodes HEIC itself (Safari, any
// iOS browser) never has the service worker fetch heic_decoder.js at all
// (see field_service_worker.js's HEIC_NATIVE_SUPPORT handler), so its row
// here reads as a plain fact rather than a cache lookup that would otherwise
// sit on "Waiting for signal" forever.
const STATUS_NOT_NEEDED = { text: "Not needed on this device", badge: "text-slate-500 bg-slate-100" }

export default class extends Controller {
  static targets = ["item", "retryAllBtn"]

  async connect() {
    this._onMessage = this._onMessage.bind(this)
    navigator.serviceWorker?.addEventListener?.("message", this._onMessage)

    await this._refresh()
  }

  disconnect() {
    navigator.serviceWorker?.removeEventListener?.("message", this._onMessage)
  }

  // Asks the active/waiting worker to retry every missing secondary item
  // right away, reusing the exact same HEAL_SHELL_CACHE message
  // field_warm_queue.js's healShellCache() sends on app open/reconnect --
  // this button is just an on-demand trigger for that same retry path
  // (field_service_worker.js's _installSecondary(), called from the
  // HEAL_SHELL_CACHE handler). Not a new mechanism.
  async retryNow() {
    const sw = navigator.serviceWorker?.controller
    if (!sw) return

    if (this.hasRetryAllBtnTarget) {
      this.retryAllBtnTarget.disabled = true
      this.retryAllBtnTarget.textContent = "Checking..."
    }

    await new Promise(resolve => {
      const channel = new MessageChannel()
      const timer = setTimeout(resolve, 15_000) // safety net, matches field_warm_queue.js's HEAL_TIMEOUT_MS
      channel.port1.onmessage = (event) => {
        if (event.data?.type === "SHELL_CACHE_HEALED") {
          clearTimeout(timer)
          resolve()
        }
      }
      sw.postMessage({ type: "HEAL_SHELL_CACHE" }, [channel.port2])
    })

    if (this.hasRetryAllBtnTarget) {
      this.retryAllBtnTarget.disabled = false
      this.retryAllBtnTarget.textContent = "Retry now"
    }

    await this._refresh()
  }

  // -- private --

  // A live SECONDARY_INSTALL_ITEM message (field_service_worker.js's
  // _notifySecondaryItem()) updates just the one row it's about, as it
  // happens -- "downloading" the moment the worker picks it up, "cached" or
  // "failed" the moment it finishes. This is what makes the page move
  // without the Tasker having to leave and come back.
  _onMessage(event) {
    if (event.data?.type !== "SECONDARY_INSTALL_ITEM") return
    const { kind, url, phase, at } = event.data
    const item = this._findItem(kind, url)
    if (!item) return

    if (phase === "downloading") {
      this._applyStatus(item, STATUS_DOWNLOADING)
      return
    }

    this._setLastAttempt(item, at)
    this._refreshOne(item).catch(() => {})
  }

  _findItem(kind, url) {
    return this.itemTargets.find(el => el.dataset.kind === kind && el.dataset.url === url) || null
  }

  async _refresh() {
    if (!("caches" in window)) return

    const shellCacheName = this._shellCacheName()
    const [shellCache, assetCache, recentLogs, heicNative] = await Promise.all([
      shellCacheName ? caches.open(shellCacheName) : null,
      caches.open(ASSET_CACHE),
      this._recentSecondaryLogs(),
      canDecodeHeicNatively(),
    ])

    this._shellCache = shellCache
    this._assetCache = assetCache
    this._recentLogs = recentLogs
    this._heicNative = heicNative

    await Promise.all(this.itemTargets.map(item => this._refreshOne(item)))
  }

  async _refreshOne(item) {
    const { kind, url } = item.dataset

    if (this._heicNative && kind === "asset" && this._isHeicDecoderAsset(url)) {
      this._applyStatus(item, STATUS_NOT_NEEDED)
      this._setMeta(item, null)
      return
    }

    const cache = kind === "page" ? this._shellCache : this._assetCache
    const matchOptions = kind === "page" ? { ignoreVary: true } : undefined

    let response = null
    try {
      response = cache ? await cache.match(url, matchOptions) : null
    } catch { /* Cache API unavailable */ }

    if (response) {
      this._applyStatus(item, STATUS_SAVED)
      this._setMeta(item, await this._sizeOf(response))
      return
    }

    const log = this._recentLogs?.get(this._logKey(kind, url))
    if (log?.outcome === "failed") {
      this._applyStatus(item, STATUS_FAILED)
      this._setLastAttempt(item, log.timestamp)
      return
    }

    if (currentLevel() <= LEVEL_DEGRADED) {
      this._applyStatus(item, STATUS_WAITING)
    } else {
      this._applyStatus(item, STATUS_CHECKING)
    }
    if (log) this._setLastAttempt(item, log.timestamp)
  }

  _applyStatus(item, status) {
    const statusEl = item.querySelector('[data-role="status"]')
    const badgeEl  = item.querySelector('[data-role="badge"]')
    if (statusEl) statusEl.textContent = status.text
    if (badgeEl)  badgeEl.className   = `shrink-0 text-xs font-medium px-2 py-1 rounded-full ${status.badge}`
  }

  _setMeta(item, text) {
    const metaEl = item.querySelector('[data-role="meta"]')
    if (metaEl) metaEl.textContent = text ? `· ${text}` : ""
  }

  _setLastAttempt(item, at) {
    if (!at) return
    const metaEl = item.querySelector('[data-role="meta"]')
    if (!metaEl) return
    const time = new Date(at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })
    metaEl.textContent = `· last tried ${time}`
  }

  // Matches heic_decoder.js's fingerprinted asset URL -- same substring check
  // as field_service_worker.js's own _isHeicDecoderAsset, kept in sync by
  // hand (the two run in different worlds: this one in page context, that
  // one with no module graph in common across the SW boundary).
  _isHeicDecoderAsset(url) {
    return url.includes("heic_decoder")
  }

  _shellCacheName() {
    const version = document.querySelector('meta[name="app-version"]')?.content
    return version ? `${SHELL_CACHE_PREFIX}${version}` : null
  }

  async _sizeOf(response) {
    try {
      const contentLength = response.headers.get("Content-Length")
      if (contentLength) return this._bytes(parseInt(contentLength, 10))
      const blob = await response.clone().blob()
      return this._bytes(blob.size)
    } catch {
      return null
    }
  }

  _bytes(n) {
    if (!n && n !== 0) return null
    if (n < 1024)        return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
  }

  // The latest 'Secondary install item cached'/'...failed' log entry per
  // url+kind, so a row still shows "Failed, will retry" (and when) across a
  // page reload, not only while this tab happens to be open for the live
  // postMessage. Scoped to the last 50 'sw' entries -- plenty for one
  // install's worth of secondary items, cheap to read.
  async _recentSecondaryLogs() {
    const map = new Map()
    try {
      const entries = await db.app_log
        .orderBy("timestamp")
        .reverse()
        .filter(r => r.category === "sw")
        .limit(50)
        .toArray()

      for (const entry of entries) {
        if (entry.message !== "Secondary install item cached" && entry.message !== "Secondary install item failed") continue
        let detail
        try { detail = JSON.parse(entry.detail) } catch { continue }
        if (!detail?.url || !detail?.kind) continue
        const key = this._logKey(detail.kind, detail.url)
        if (map.has(key)) continue // first hit per key is the most recent (reverse order)
        map.set(key, {
          outcome: entry.message.endsWith("cached") ? "cached" : "failed",
          timestamp: entry.timestamp
        })
      }
    } catch { /* IndexedDB unavailable */ }
    return map
  }

  _logKey(kind, url) {
    return `${kind}:${url}`
  }
}
